import { useTranslations } from 'use-intl';

import {
  MergedSubscriptionStepUnion,
  SubscriptionStepStatus,
  SubscriptionStepType,
} from '@blockpulse3/graphql/hooks';

type SubscriptionStep = {
  index: number;
  status: SubscriptionStepStatus | undefined;
  title: string;
  type: SubscriptionStepType;
};

export function useStepStatusMap(
  mergedSteps: MergedSubscriptionStepUnion[] | null | undefined,
): [Record<SubscriptionStepType, SubscriptionStepStatus | undefined>, SubscriptionStep[]] {
  const i18nSubscriberDetailsSidePanel = useTranslations('SubscriberDetailsSidePanel');

  if (!mergedSteps)
    return [{} as Record<SubscriptionStepType, SubscriptionStepStatus | undefined>, []];

  const stepStatusMap = mergedSteps.reduce(
    (acc, step) => {
      if ('type' in step) {
        acc[step.type] = step.status;
      }
      return acc;
    },
    {} as Record<SubscriptionStepType, SubscriptionStepStatus | undefined>,
  );

  const stepTitles: Record<SubscriptionStepType, string> = {
    [SubscriptionStepType.MIFID]: i18nSubscriberDetailsSidePanel('ValidationSteps.Mifid.Title'),
    [SubscriptionStepType.IDENTITY_VERIFICATION]: i18nSubscriberDetailsSidePanel(
      'ValidationSteps.IdentityVerification',
    ),
    [SubscriptionStepType.HOLDING_METHOD]: i18nSubscriberDetailsSidePanel(
      'ValidationSteps.HoldingMethod',
    ),
    [SubscriptionStepType.DETAILS]: i18nSubscriberDetailsSidePanel(
      'ValidationSteps.SubscriptionDetails.Title',
    ),
    [SubscriptionStepType.DOCUMENT_SIGNATURE]: i18nSubscriberDetailsSidePanel(
      'ValidationSteps.DocumentSignature',
    ),
    [SubscriptionStepType.PAYMENT]: i18nSubscriberDetailsSidePanel('ValidationSteps.Payment.Title'),
  };

  const steps = mergedSteps
    .filter(({ type }) => type !== SubscriptionStepType.HOLDING_METHOD)
    .map(({ status, type }, index) => ({
      index,
      status,
      title: stepTitles[type],
      type,
    }));

  return [stepStatusMap, steps];
}

export function useStepStatusMapLazy(): (
  mergedSteps: MergedSubscriptionStepUnion[] | null | undefined,
) => [Record<SubscriptionStepType, SubscriptionStepStatus | undefined>, SubscriptionStep[]] {
  return useStepStatusMap;
}
